<template>
  <div
    v-if="active"
    class="dropdown"
  >
    <div
      v-if="initiated || loading || noResults || timeout"
      class="dropdown__info"
    >
      <p v-if="initiated && !loading">{{ type === 'suggestions' ? 'Loading...' : 'Select field first' }}</p>
      <p v-else-if="loading">Loading...</p>
      <p v-else-if="noResults">No results!<br><span>Try something else...</span></p>
      <p v-else-if="timeout">Timeout!<br><span>Please try again later...</span></p>
    </div>

    <UtilityScroll
      v-else
      :height="getScrollHeight()"
      variant="dark"
    >
      <div class="dropdown__list">
        <div
          v-for="(option, index) in options"
          :key="'OPTION__' + option.key"
          :class="['dropdown__item', scrollPosition === index ? 'dropdown__item--selected' : '']"
          @mouseover="$emit('positionChanged', index); $emit('tipChanged', option.tip)"
          @mouseout="$emit('positionChanged', false); $emit('tipChanged', false)"
          @click.stop="$emit('valueChanged', option, true)"
        >
          <div
            v-if="noIcons === false && option.icon"
            :class="['dropdown__icon', 'dropdown__icon--' + iconSize]"
            v-html="option.icon"
          />

          <div
            class="dropdown__text"
            v-html="highlight(option.results ? option.name + ' <span class=\'dropdown__text--results\'>(' + formatNumber(option.results, 0) + ')</span>' : option.name, inputValue)"
          />
        </div>
      </div>
    </UtilityScroll>
  </div>
</template>

<script>
import UtilityScroll from '@/components/utility/Scroll.vue'
import helperMethods from '@/helpers/utility.js'

export default {
  components: {
    UtilityScroll
  },
  props: {
    inputValue: {
      type: [Boolean, String],
      default: ''
    },
    options: {
      type: [Boolean, Object, Array],
      default: false
    },
    type: {
      type: String,
      default: 'text'
    },
    refName: {
      type: String,
      default: 'input'
    },
    iconSize: {
      type: String,
      default: 'md',
      validator (values) {
        return ['sm', 'md', 'lg'].includes(values)
      }
    },
    variant: {
      type: String,
      default: 'dark',
      validator (values) {
        return ['dark', 'lightdark'].includes(values)
      }
    },
    noIcons: {
      type: Boolean,
      default: false
    },
    rounded: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    active: {
      type: Boolean,
      default: false
    },
    scrollPosition: {
      type: Number,
      default: 0
    },
    initiated: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    timeout: {
      type: Boolean,
      default: false
    },
    noResults: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      windowWidth: window.innerWidth
    }
  },
  methods: {
    getScrollHeight () {
      let height = '320px'

      if (this.windowWidth >= 1200) {
        height = '340px'
      }

      return height
    },
    resize () {
      this.windowWidth = window.innerWidth
    },
    ...helperMethods
  },
  created () {
    window.addEventListener('resize', this.resize)
  },
  unmounted () {
    window.removeEventListener('resize', this.resize)
  },
  mounted () {
    this.resize()
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/app.scss';

.dropdown {
  @include font('content--sm');
  width: calc(100% - 1px);
  height: auto;
  max-height: inherit;
  overflow: auto;
  background-color: $bg-2;
  border-top: 1px solid $black;
  border-bottom: 1px solid $black;
  user-select: none;
  cursor: arrow;

  @media screen and (min-width: $breakpointLg) {
    @include font('content--md');
    //max-height: 339px;
  }

  &__info p {
    padding: 8px;
    margin: 0;
    text-align: left;
    border-top: 1px solid rgba($white, 0.1);

    span {
      color: rgba($white, 0.7);
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
  }

  &__item {
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    border-top: 1px solid rgba($white, 0.1);
    border-bottom: 1px solid $black;
    border-left: 4px solid rgba($white, 0.1);
    padding: 5px 0px;

    &:hover, &--selected {
      background-color: $bg-4;
      border-left-color: $blue;

      .dropdown__icon {
        fill: rgba($white, 0.7);
      }
    }

    &:last-child {
      border-bottom: none;
    }
  }

  &__icon {
    fill: rgba($white, 0.2);
    transition: fill 0.2s ease-out;
    margin-left: 4px;

    &--sm {
      width: 18px;
      height: 18px;

      @media screen and (min-width: $breakpointLg) {
        width: 20px;
        height: 20px;
      }
    }

    &--md, &--lg {
      width: 20px;
      height: 20px;

      @media screen and (min-width: $breakpointLg) {
        width: 22px;
        height: 22px;
      }
    }
  }

  &__text {
    flex: 1;
    margin: 0 12px 0 8px;
    text-align: left;

    /deep/ &--results {
      color: rgba($white, 0.5);
    }
  }
}

:-webkit-autofill {
  box-shadow: 0 0 0 100rem darken($bg-2, 2) inset !important;
  -webkit-text-fill-color: $white !important;
}

</style>
